<template>
  <span :class="['p-toast-message-icon', 'error-toast__icon', severityIcon]"></span>
  <div class="p-toast-message-text">
    <span class="p-toast-summary">{{ message.summary }}</span>
    <div v-if="message.detail" class="p-toast-detail">{{ message.detail }}</div>

    <div v-if="errorData" class="error-toast__id">
      <span class="error-toast__id-label">{{ errorData.tracingIdPrefix }}:</span>
      <code class="error-toast__id-value" data-testid="toast-error-id">{{ errorData.tracingId }}</code>
      <button
        type="button"
        class="error-toast__copy"
        :title="errorData.copyLabel"
        :aria-label="errorData.copyLabel"
        data-testid="toast-copy-error-id"
        @click.stop="onCopy"
      >
        <i :class="copied ? 'pi pi-check' : 'pi pi-copy'"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { copyToClipboard } from '../helpers/clipboard';
import {
  getToastInstance,
  successToast,
  ToastLifetime,
  ToastSeverity,
  type ErrorToastMessageOptions,
} from '../helpers/toastHelpers';

const props = defineProps<{ message: ErrorToastMessageOptions }>();

// Ikonu koju PrimeVue inače sam ubaci moramo nacrtati sami jer #message template
// zamjenjuje cijeli sadržaj toasta.
function iconForSeverity(severity: string | undefined): string {
  switch (severity) {
    case ToastSeverity.SUCCESS:
      return 'pi pi-check';
    case ToastSeverity.WARNING:
      return 'pi pi-exclamation-triangle';
    case ToastSeverity.ERROR:
      return 'pi pi-times-circle';
    default:
      return 'pi pi-info-circle';
  }
}

const COPIED_FEEDBACK_MS = 2000;

const copied = ref(false);
let copiedTimeout: ReturnType<typeof setTimeout> | null = null;

const severityIcon = computed(() => iconForSeverity(props.message.severity));

// Prikazuje se samo kod grešaka koje imaju tracingId (5xx) - ostali toastovi ostaju nepromijenjeni.
const errorData = computed(() => props.message.data);

async function onCopy() {
  const data = errorData.value;
  if (!data) return;

  const toast = getToastInstance();
  const success = await copyToClipboard(data.tracingId);

  if (!success) {
    toast.add({
      severity: ToastSeverity.WARNING,
      summary: data.copyFailedMessage,
      life: ToastLifetime.MEDIUM,
    });
    return;
  }

  copied.value = true;
  if (copiedTimeout) clearTimeout(copiedTimeout);
  copiedTimeout = setTimeout(() => {
    copied.value = false;
  }, COPIED_FEEDBACK_MS);

  successToast(data.copiedMessage, toast);
}

onBeforeUnmount(() => {
  if (copiedTimeout) clearTimeout(copiedTimeout);
});
</script>

<style scoped>
.error-toast__icon {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.error-toast__id {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.error-toast__id-label {
  font-size: 0.75rem;
  opacity: 0.85;
}

.error-toast__id-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  /* Cijeli ID mora biti označiv u jednom potezu ako korisnik kopira ručno. */
  user-select: all;
  word-break: break-all;
}

.error-toast__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}

.error-toast__copy:hover {
  background-color: color-mix(in srgb, currentColor 15%, transparent);
}

.error-toast__copy:focus-visible {
  outline: 1px solid currentColor;
  outline-offset: 1px;
}

.error-toast__copy .pi {
  font-size: 0.875rem;
}
</style>
